import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useApp } from '../contexts/AppContext';
import { TabbedPageLayout } from '../components';
import Overview from './app-detail/Overview';
import CostTab from './app-detail/CostTab';
import PerformanceTab from './app-detail/PerformanceTab';
import TelemetryTab from './app-detail/TelemetryTab';
import JourneysTab from './app-detail/JourneysTab';
import ClassifyTab from './app-detail/classify/ClassifyTab';
import RcaSpaceTab from './app-detail/RcaSpaceTab';
import Guard from './app-detail/guard/Guard';
import {
  isInDemoMode
} from '../services/demoDataService';
import SimulationStudioDemo from './app-detail/SimulationStudioDemo';
import SimulationStudio from './app-detail/SimulationStudio';
import ErrorBoundary from '../components/ErrorBoundary';

/**
 * AppDetails - Application detail page with tabs
 *
 * URL: /app/:selectedApp?tab=overview
 * Tabs: Overview (default), Cost, Performance, Telemetry, Journeys, Signals
 * Hamburger menu: RCA, Simulation Studio
 *
 * Features:
 * - Breadcrumb back to Home
 * - TabbedPageLayout with all three global selectors
 * - Stats bar with app-specific metrics
 * - KPI bar with app-level KPIs
 * - Tab content switches based on URL query param 'tab'
 */
export default function AppDetails() {
  const { selectedAppId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { displayLabel, selectedRange } = useTimeRange();
  const { selectedEnvironment: environment, environments } = useEnvironment();
  const { updateApp } = useApp();
  const { session } = useStytchMemberSession();

  // Tab state from URL param (default to 'overview')
  const activeTab = searchParams.get('tab') || 'overview';

  // Default signal key — set when navigating from Overview signal spike to Signals tab
  const [defaultSignalKey, setDefaultSignalKey] = useState(null);

  // Get tenant ID for demo mode check
  const tenantId = useMemo(() => {
    return session?.organization_id || null;
  }, [session]);

  // Check if in demo mode
  const isDemo = useMemo(() => {
    return isInDemoMode(tenantId, environment, selectedAppId);
  }, [tenantId, environment, selectedAppId]);

  // Get app_name from environments context as a cached fallback; v2 service detail
  // is the authoritative source but environments may already have the name loaded.
  const appName = useMemo(() => {
    const env = environments.find(e => e.name === environment);
    const match = env?.apps?.find(a => a.id === selectedAppId);
    return match?.name || selectedAppId;
  }, [environments, environment, selectedAppId]);

  // Pagination state (for list tabs)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;

  // Breadcrumb for navigation back to Home (preserve env and range params)
  const breadcrumbItems = useMemo(() => {
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (selectedRange) params.set('range', selectedRange);

    return [
      {
        label: appName,
      },
    ];
  }, [appName, environment, selectedRange]);

  // Tab definitions
  const tabs = useMemo(() => ([
    { key: 'overview', label: 'Problems' },
    { key: 'cost', label: 'Cost' },
    { key: 'performance', label: 'Performance' },
    { key: 'agents', label: 'Agents' },
    { key: 'telemetry', label: 'Explore' },
    { key: 'classify', label: 'Signals', align: 'end' },
  ]), []);

  // Secondary views tucked behind the hamburger menu, to the right of the tabs
  const menuItems = useMemo(() => ([
    { key: 'rca', label: 'RCA' },
    { key: 'simulation', label: 'Simulation Studio' },
  ]), []);

  // Navigate from Overview signal spike to Signals tab with pre-selection
  const handleSignalClick = (classifierKey) => {
    setDefaultSignalKey(classifierKey);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'classify');
    setSearchParams(newParams);
  };

  // Populate AppContext with app name (mirrors EnvironmentContext pattern)
  useEffect(() => {
    updateApp(selectedAppId, appName, []);
  }, [selectedAppId, appName, updateApp]);

  // Handle tab change - update URL param
  const handleTabChange = (tabKey) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabKey);
    // Telemetry and Journeys both use `subtab`; clearing it on a top-level tab
    // switch avoids carrying a stale sub-tab across tabs (and prevents the
    // old-deep-link redirect in TelemetryTab from bouncing a fresh tab click).
    newParams.delete('subtab');
    setSearchParams(newParams);
    setCurrentPage(1); // Reset pagination when switching tabs
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // TODO: Fetch data for the new page
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <Overview
            selectedAppId={selectedAppId}
            environment={environment}
            timeWindow={selectedRange}
            timeLabel={displayLabel}
            isDemo={isDemo}
            onSignalClick={handleSignalClick}
          />
        );
      case 'cost':
        return <CostTab />;
      case 'performance':
        return <PerformanceTab />;
      case 'telemetry':
        return <TelemetryTab />;
      case 'agents':
        return <JourneysTab />;
      case 'classify':
        return (
          <ClassifyTab
            appId={selectedAppId}
            environment={environment}
            timeLabel={displayLabel}
            selectedRange={selectedRange}
            defaultSignalKey={defaultSignalKey}
            onDefaultSignalConsumed={() => setDefaultSignalKey(null)}
            isDemo={isDemo}
          />
        );
      case 'rca':
        return (
          <RcaSpaceTab
            appId={selectedAppId}
            environment={environment}
            selectedRange={selectedRange}
          />
        );
      case 'simulation':
        if (isDemo) {
          return <SimulationStudioDemo appId={selectedAppId} environment={environment} />;
        }
        return <SimulationStudio appId={selectedAppId} environment={environment} />;
      default:
        return null;
    }
  };

  return (
    <TabbedPageLayout
      breadcrumbItems={null}
      title={appName}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      menuItems={menuItems}
      kpis={undefined}
      loading={false}
      error={null}
      info={null}
      data={null}
      currentPage={undefined}
      itemsPerPage={undefined}
      totalItems={undefined}
      onPageChange={undefined}
    >
      {/* Per-tab boundary: a crashing tab degrades to an inline card instead of
          blanking the console. Keyed by tab so switching tabs resets the error. */}
      <ErrorBoundary
        key={activeTab}
        fallback={(
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="text-base font-medium text-text-primary mb-1">This tab hit an error</div>
              <div className="text-sm text-text-supporting">Switch tabs or refresh. Other tabs are unaffected.</div>
            </div>
          </div>
        )}
      >
        {renderTabContent()}
      </ErrorBoundary>
    </TabbedPageLayout>
  );
}
